import React from 'react'

import clsx from 'clsx'

import useForm from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { RHFInput } from 'react-hook-form-input'

import {
  Button,
  Divider,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  makeStyles,
  Link,
  Box,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'

import { Delete as DeleteIcon } from '@material-ui/icons'

import { InputAutocomplete, InputCurrency } from 'components/Form'
import * as service from 'service'
import helpers from 'helpers'
import useCollapse from 'hooks/useCollapse'
import useSnackbar from 'hooks/useSnackbar'
import useConfirmDialog from 'hooks/useConfirmDialog'
import styles from './styles'
import schemas from './../../../../schemas'
import { Alert } from '@material-ui/lab'
import useRecurringCheck from 'hooks/useRecurringCheck'

const useStyles = makeStyles(styles)

const CollapseBody = ({ item }) => {
  const classes = useStyles()
  const params = useParams()
  const collapse = useCollapse()
  const snackbar = useSnackbar()
  const confirmDialog = useConfirmDialog()
  const {
    setQuantityChecked,
    handleChangeCheck,
    handleChangeQuantity,
    checkRecurrentValue,
  } = useRecurringCheck()
  const inputQuantityRef = React.useRef()

  const {
    register,
    setValue,
    errors,
    handleSubmit,
    unregister,
    watch,
  } = useForm({
    validationSchema: schemas.contractPlan,
    defaultValues: {
      ...item,
      quantity: String(item.quantity),
      'contractPlanEntries[0].id': item.contractPlanEntries[0].id,
      'contractPlanEntries[0].quantity':
        item.contractPlanEntries[0].quantity !== undefined
          ? String(item.contractPlanEntries[0].quantity)
          : '',
      'contractPlanEntries[0].price': item.contractPlanEntries[0].price,
    },
  })

  const values = watch()

  const getDefaultIdPresence = () => {
    return String(item.contractPlanEntries[0].quantity) === '0' ||
      item.contractPlanEntries[0].quantity > 0
      ? true
      : false
  }

  const [removable, setRemovable] = React.useState(getDefaultIdPresence)

  const onSubmit = async contractPlan => {
    collapse.update(item.collapseId, {
      ...contractPlan,
      removable,
      contractPlanEntries: helpers.contractPlanEntries.normalizeReceived(
        contractPlan.contractPlanEntries,
      ),
      collapseSettings: { expand: false, edit: false, updatedOnce: true },
    })
  }

  const handleChangePlan = event => {
    const plan = event.target.value

    if (!plan) {
      setValue('periodicity', '')
      setValue('quantity', '')
      setValue('contractPlanEntries[0].id', '')
      setValue('contractPlanEntries[0].quantity', '')
      setValue('contractPlanEntries[0].price', 0)
      setValue('price', 0)
      setQuantityChecked({})
      return
    }

    setValue('periodicity', plan.periodicity, true)
    setValue('price', plan.price, true)
    setImmediate(() => inputQuantityRef.current.focus())
  }

  const handleClickEditButton = event => {
    event.preventDefault()

    collapse.toggleEdit(item.collapseId)
  }

  const handleClickRemove = event => {
    event.preventDefault()
    setValue('contractPlanEntries[0].quantity', '')
    setValue('contractPlanEntries[0].price', 0)
    setRemovable(false)
    unregister('contractPlanEntries[0].quantity')
    unregister('contractPlanEntries[0].price')
  }
  const handleClickAdd = event => {
    event.preventDefault()

    setRemovable(true)

    setQuantityChecked({
      quantity: helpers.contractPlans.isRecurring(values.quantity),
      'contractPlanEntries[0].quantity': helpers.contractPlanEntries.isRecurring(
        values['contractPlanEntries[0].quantity'],
      ),
    })
  }

  const handleClickCancelButton = event => {
    event.preventDefault()

    const prevItem = collapse.get(item.collapseId)

    setValue('periodicity', prevItem.periodicity, true)
    setValue('quantity', prevItem.quantity.toString(), true)
    setValue('price', prevItem.price, true)
    setRemovable(getDefaultIdPresence)

    if (!getDefaultIdPresence()) {
      unregister('contractPlanEntries[0].quantity')
      unregister('contractPlanEntries[0].price')
    }

    if (getDefaultIdPresence()) {
      prevItem.contractPlanEntries.forEach((contractPlanEntry, index) => {
        setValue(`contractPlanEntries[${index}].id`, contractPlanEntry.id, true)
        setValue(
          `contractPlanEntries[${index}].quantity`,
          String(contractPlanEntry.quantity),
          true,
        )
        setValue(
          `contractPlanEntries[${index}].price`,
          contractPlanEntry.price,
          true,
        )
        setQuantityChecked({
          quantity: helpers.contractPlans.isRecurring(prevItem.quantity),
          [`contractPlanEntries[${index}].quantity`]: helpers.contractPlanEntries.isRecurring(
            contractPlanEntry.quantity,
          ),
        })
      })
    }

    collapse.toggleEdit(item.collapseId)
  }

  const handleClickRemoveButton = async () => {
    if (!item.id) {
      collapse.remove(item.collapseId)
      return
    }

    confirmDialog.open({
      title: 'Inativar plano',
      message: 'Deseja realmente inativar este plano?',
      cancelButton: 'Cancelar',
      confirmButton: 'Inativar',
      handleConfirm: async e => {
        try {
          await service.recurrence.contractPlans.inactivate({
            contractId: params.contractId,
            contractPlanId: item.id,
          })

          collapse.remove(item.collapseId)
          snackbar.open({
            message: 'Plano inativado com sucesso!',
            variant: 'success',
          })
        } catch (error) {
          snackbar.open({
            message: 'Erro ao inativar plano!',
            variant: 'error',
          })
        }
      },
    })
  }

  const showDeleteButton =
    collapse.data.filter(item => item.collapseSettings.updatedOnce).length >
      1 || !item.collapseSettings.updatedOnce

  const contractPlanFinished = item.status === 'finished'
  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      <div className={classes.container}>
        <div
          className={clsx(classes.rowInput, {
            [classes.hide]: item.id,
          })}
        >
          <RHFInput
            as={
              <InputAutocomplete
                fn={service.recurrence.plans.get}
                fnLabel={option => option.name || ''}
                entity="plans"
                keyEntity="id"
                searchParam="plan[name]"
                fixedFilter={{ perPage: 10 }}
                textFieldProps={{
                  label: 'Planos',
                  margin: 'dense',
                  variant: 'outlined',
                  fullWidth: true,
                  error: !!errors.plan,
                  helperText: errors?.plan?.message,
                }}
                className={`${classes.input} ${classes.inputHalf}`}
                size="small"
              />
            }
            name="plan"
            register={register}
            setValue={setValue}
            onChange={handleChangePlan}
            mode="onChange"
          />
        </div>

        <Box mt={2}>
          <Typography color="textSecondary">
            Periodicidade da cobrança (de quanto em quanto tempo será cobrado?)
          </Typography>
        </Box>
        <RHFInput
          as={
            <TextField
              label="Periodicidade"
              className={`${classes.input} ${classes.inputHalf}`}
              SelectProps={{ native: true }}
              size="small"
              margin="dense"
              select
              error={errors.periodicity !== undefined}
              helperText={errors.periodicity?.message}
              disabled={!item.collapseSettings.edit}
              fullWidth
            >
              <option></option>
              {helpers.plans.periodicityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          }
          name="periodicity"
          register={register}
          setValue={setValue}
          mode="onChange"
        />

        <div className={classes.rowInput}>
          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Quantas vezes será cobrado? (Ex: 12)
              </Typography>
            </Box>
            <RHFInput
              as={
                <TextField
                  label="Quantidade"
                  type="number"
                  margin="dense"
                  fullWidth
                  inputRef={inputQuantityRef}
                  className={classes.input}
                  error={!!errors.quantity}
                  helperText={errors?.quantity?.message}
                  inputProps={{ min: 0 }}
                  disabled={
                    !item.collapseSettings.edit ||
                    checkRecurrentValue('quantity', item.quantity)
                  }
                />
              }
              name="quantity"
              register={register}
              setValue={setValue}
              onChange={e => handleChangeQuantity(e, 'quantity')}
              mode="onChange"
            />
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={'Recorrente (tempo indeterminado)'}
              size="small"
              onChange={e =>
                handleChangeCheck(e, 'quantity', setValue, item.quantity)
              }
              checked={checkRecurrentValue('quantity', item.quantity)}
              disabled={!item.collapseSettings.edit}
            />
          </Box>

          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Valor de cada cobrança
              </Typography>
            </Box>
            <RHFInput
              as={
                <InputCurrency
                  label="Valor"
                  margin="dense"
                  fullWidth
                  className={classes.input}
                  error={!!errors.price}
                  helperText={errors && errors.price && errors.price.message}
                  disabled={!item.collapseSettings.edit}
                />
              }
              name="price"
              register={register}
              setValue={setValue}
              mode="onChange"
            />
          </Box>
        </div>
        {!helpers.contractPlans.isRecurring(item.quantity) &&
          item.quantityLaunched !== undefined && (
            <div className={classes.rowInput}>
              <Box mt={2}>
                <Typography color="textSecondary" variant="body2">
                  Quantidade de cobranças que já foram feitas:{' '}
                  <strong>{item.quantityLaunched}</strong>
                </Typography>
              </Box>
            </div>
          )}
        {item.contractPlanEntries.map((contractPlanEntry, index) => (
          <div key={index}>
            <Box mt={2} className={classes.discountBox}>
              <Typography className={classes.discountTitle} variant="h6">
                Desconto
              </Typography>
            </Box>
            {item.collapseSettings.edit && (
              <RHFInput
                as={<TextField type="hidden" className={classes.hide} />}
                name={`contractPlanEntries[${index}].id`}
                register={register}
                unregister={unregister}
                setValue={setValue}
                mode="onChange"
              />
            )}
            {removable ? (
              <div>
                <div className={classes.rowInput}>
                  <Box className={`${classes.input} ${classes.inputHalf}`}>
                    <Box mt={2}>
                      <Typography color="textSecondary">
                        Quantas cobranças terão desconto? (Ex: 3)
                      </Typography>
                    </Box>
                    <RHFInput
                      as={
                        <TextField
                          label="Quantidade"
                          type="number"
                          margin="dense"
                          fullWidth
                          onChange={e => {
                            handleChangeQuantity(
                              e,
                              `contractPlanEntries[${index}].quantity`,
                            )
                          }}
                          className={classes.input}
                          error={
                            !!errors[`contractPlanEntries[${index}].quantity`]
                          }
                          helperText={
                            errors &&
                            errors[`contractPlanEntries[${index}].quantity`] &&
                            errors[`contractPlanEntries[${index}].quantity`]
                              .message
                          }
                          inputProps={{ min: 0 }}
                          disabled={
                            !item.collapseSettings.edit ||
                            checkRecurrentValue(
                              `contractPlanEntries[${index}].quantity`,
                              contractPlanEntry.quantity,
                            )
                          }
                        />
                      }
                      name={`contractPlanEntries[${index}].quantity`}
                      register={register}
                      setValue={setValue}
                      onChange={e => {
                        handleChangeQuantity(
                          e,
                          `contractPlanEntries[${index}].quantity`,
                        )
                      }}
                      mode="onChange"
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" />}
                      label={'Recorrente (tempo indeterminado)'}
                      size="small"
                      onChange={e =>
                        handleChangeCheck(
                          e,
                          `contractPlanEntries[${index}].quantity`,
                          setValue,
                          Number(contractPlanEntry.quantity)
                            ? contractPlanEntry.quantity
                            : '',
                        )
                      }
                      checked={checkRecurrentValue(
                        `contractPlanEntries[${index}].quantity`,
                        contractPlanEntry.quantity,
                      )}
                      disabled={!item.collapseSettings.edit}
                    />
                  </Box>

                  <Box className={`${classes.input} ${classes.inputHalf}`}>
                    <Box mt={2}>
                      <Typography color="textSecondary">
                        Informe o valor do desconto
                      </Typography>
                    </Box>
                    <RHFInput
                      as={
                        <InputCurrency
                          label="Valor"
                          margin="dense"
                          fullWidth
                          className={classes.input}
                          error={
                            !!errors[`contractPlanEntries[${index}].price`]
                          }
                          helperText={
                            errors &&
                            errors[`contractPlanEntries[${index}].price`] &&
                            errors[`contractPlanEntries[${index}].price`]
                              .message
                          }
                          disabled={!item.collapseSettings.edit}
                        />
                      }
                      name={`contractPlanEntries[${index}].price`}
                      register={register}
                      setValue={setValue}
                      mode="onChange"
                    />
                  </Box>
                </div>
                {!helpers.contractPlanEntries.isRecurring(
                  contractPlanEntry.quantity,
                ) &&
                  contractPlanEntry.quantityLaunched !== undefined && (
                    <div className={classes.rowInput}>
                      <Box mt={2}>
                        <Typography color="textSecondary" variant="body2">
                          Quantidade de descontos que foram aplicados:{' '}
                          <strong>{contractPlanEntry.quantityLaunched}</strong>
                        </Typography>
                      </Box>
                    </div>
                  )}
                {item.collapseSettings.edit && (
                  <div className={classes.discountFooterLink}>
                    <Link
                      href="#"
                      variant="overline"
                      onClick={handleClickRemove}
                      className={classes.link}
                    >
                      Remover desconto
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <Box className={classes.emptyDiscountFooterBox}>
                <Typography
                  variant="body2"
                  className={classes.discountFooterItem}
                >
                  Nenhum desconto adicionado
                </Typography>
                {item.collapseSettings.edit && (
                  <div className={classes.discountFooterLink}>
                    <Link
                      href="#"
                      variant="overline"
                      onClick={handleClickAdd}
                      className={classes.link}
                    >
                      Adicionar desconto
                    </Link>
                  </div>
                )}
              </Box>
            )}
          </div>
        ))}
      </div>

      <Divider />

      {!contractPlanFinished && (
        <div className={classes.buttonGroup}>
          <div>
            {item.collapseSettings.edit ? (
              <>
                <Box my={2}>
                  <Alert severity="info">
                    <Typography color="textSecondary">
                      Lembre-se de salvar as alterações de planos na parte
                      inferior da tela.
                    </Typography>
                  </Alert>
                </Box>
                <Box display="flex">
                  <Box maxWidth={150} className={classes.buttonSave}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                    >
                      Concluir
                    </Button>
                  </Box>
                  {item.id && (
                    <Box maxWidth={150}>
                      <Button
                        variant="outlined"
                        color="primary"
                        type="button"
                        fullWidth
                        onClick={handleClickCancelButton}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="button"
                onClick={handleClickEditButton}
              >
                Editar
              </Button>
            )}
          </div>

          {collapse.data.length > 1 && showDeleteButton && (
            <Tooltip
              title={item.collapseSettings.updatedOnce ? 'Inativar' : 'Remover'}
            >
              <IconButton
                aria-label="Remover"
                onClick={handleClickRemoveButton}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
    </form>
  )
}

export default CollapseBody
